import React, { useState, useCallback } from 'react';
import { ApolloProvider, gql, useQuery, useMutation } from '@apollo/client';
import client from '../lib/apolloClient';
import MarketplaceBrowsePage from '../components/Succeedo/Marketplace/MarketplaceBrowsePage';

const SEARCH_QUERY = gql`
  query SearchMarketplaceListings(
    $industry: [String]
    $province: [String]
    $revenue_range: [String]
    $employee_count_range: [String]
    $asking_price_range: [String]
    $timeline: [String]
    $q: String
    $sort: String
    $page: Int
    $per_page: Int
  ) {
    currentUser {
      id
      name
      succeedo_persona
    }
    search_deal_listings(
      industry: $industry
      province: $province
      revenue_range: $revenue_range
      employee_count_range: $employee_count_range
      asking_price_range: $asking_price_range
      timeline: $timeline
      q: $q
      sort: $sort
      page: $page
      per_page: $per_page
    ) {
      items {
        id
        industry
        province
        revenue_range_bucket
        employee_count_bucket
        timeline
        asking_price
        deal_state
      }
      total_count
      page
      per_page
    }
  }
`;

const CREATE_SAVED_SEARCH = gql`
  mutation CreateSavedSearch($name: String!, $filters: JSON, $alert_frequency: String) {
    create_saved_search(name: $name, filters: $filters, alert_frequency: $alert_frequency) {
      saved_search
      errors
    }
  }
`;

function deriveInitials(name) {
  if (!name) return '??';
  const parts = name.trim().split(/\s+/);
  return ((parts[0]?.[0] || '') + (parts.length > 1 ? parts[parts.length - 1][0] : '')).toUpperCase();
}

function MarketplaceLoader() {
  const [filters, setFilters] = useState({});
  const { data, loading } = useQuery(SEARCH_QUERY, {
    variables: { ...filters, page: 1, per_page: 20 },
    fetchPolicy: 'cache-and-network',
  });

  const [createSavedSearch] = useMutation(CREATE_SAVED_SEARCH);

  const onSaveSearch = useCallback(async ({ name, alert_frequency, filters: savedFilters }) => {
    const { data: result } = await createSavedSearch({
      variables: { name, alert_frequency, filters: savedFilters || {} },
    });
    const errors = result?.create_saved_search?.errors || [];
    if (errors.length) {
      // Throwing here surfaces the error inside the modal — Page catches
      // and renders it under the form rather than alerting blockingly.
      throw new Error(errors.join(', '));
    }
    try { window.ahoy?.track('succeedo_marketplace_search_saved', { name }); } catch (_e) {}
    if (typeof window !== 'undefined' && window.confirm('Search saved! Visit your Saved Searches now?')) {
      window.location.href = '/succeedo/marketplace/saved-searches';
    }
  }, [createSavedSearch]);

  const onApplyFilters = useCallback((next) => {
    const cleaned = {};
    if (Array.isArray(next.industry) && next.industry.length) cleaned.industry = next.industry;
    if (Array.isArray(next.province) && next.province.length) cleaned.province = next.province;
    if (next.q) cleaned.q = next.q;
    if (next.revenue_range)      cleaned.revenue_range      = [next.revenue_range];
    if (next.asking_price_range) cleaned.asking_price_range = [next.asking_price_range];
    if (next.timeline)           cleaned.timeline           = [next.timeline];
    if (next.sort && next.sort !== 'newest') cleaned.sort = next.sort;
    setFilters(cleaned);
  }, []);

  React.useEffect(() => {
    if (typeof window !== 'undefined' && window.ahoy?.track) {
      try { window.ahoy.track('succeedo_marketplace_viewed'); } catch (_e) {}
    }
  }, []);

  const user   = data?.currentUser;
  const result = data?.search_deal_listings || { items: [], total_count: 0 };

  return (
    <MarketplaceBrowsePage
      listings={result.items}
      totalCount={result.total_count}
      loading={loading}
      persona={(user?.succeedo_persona || 'buyer').toUpperCase()}
      userName={user?.name || ''}
      userInitials={deriveInitials(user?.name)}
      onApplyFilters={onApplyFilters}
      onSaveSearch={onSaveSearch}
    />
  );
}

export default function SucceedoMarketplaceContainer() {
  return (
    <ApolloProvider client={client}>
      <MarketplaceLoader />
    </ApolloProvider>
  );
}
